import { css } from '@emotion/react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonSearchbar,
  IonToolbar,
} from '@ionic/react';
import React from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectWithFilterProps {
  options: SelectOption[];
  initialOption?: SelectOption;
  onSelectOption?: (option?: SelectOption) => void;
}

export const SelectWithFilter: React.FC<SelectWithFilterProps> = ({ options, initialOption, onSelectOption }) => {
  const [tempOption, setTempOption] = React.useState<SelectOption | undefined>(initialOption);
  const [selectedOption, setSelectedOption] = React.useState<SelectOption | undefined>(initialOption);
  const [showModal, setShowModal] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');

  const handleCancel = React.useCallback(() => {
    setTempOption(selectedOption);
    setShowModal(false);
  }, [selectedOption]);

  const handleOk = React.useCallback(() => {
    setSelectedOption(tempOption);
    onSelectOption?.(tempOption);
    setShowModal(false);
  }, [tempOption]);

  const handleShowModal = React.useCallback(() => {
    setTempOption(selectedOption);
    setShowModal(true);
  }, [selectedOption]);

  const handleChangeRadio = React.useCallback(
    (e: CustomEvent) => {
      const value = e.detail.value;
      const option = options.find((opt) => opt.value === value);
      if (option) {
        setTempOption(option);
      }
    },
    [options]
  );

  const filteredOptions = React.useMemo(
    () => options.filter(({ label }) => label.toLowerCase().includes(filterText.toLowerCase())),
    [options, filterText]
  );

  const selectWithFilterCss = {
    button: css({
      width: '14rem',
      marginLeft: '-1rem',
    }),
    modal: css({
      '--border-radius': '8px',
      '--width': '320px',
      '--height': '320px',
    }),
    list: css({
      maxHeight: '200px',
      overflowY: 'auto',
    }),
  };

  return (
    <>
      <IonItem button detail={true} lines="none" onClick={handleShowModal} css={selectWithFilterCss.button}>
        <IonLabel>{selectedOption?.label || 'Select an option'}</IonLabel>
      </IonItem>

      <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)} css={selectWithFilterCss.modal}>
        <IonContent>
          <IonSearchbar onIonInput={(e) => setFilterText(e.detail.value!)} mode="ios" debounce={250} />
          <IonList css={selectWithFilterCss.list}>
            <IonRadioGroup onIonChange={handleChangeRadio} value={tempOption?.value}>
              {filteredOptions.length === 0 ? (
                <IonListHeader>No matches found</IonListHeader>
              ) : (
                filteredOptions.map(({ label, value }) => (
                  <IonItem key={value}>
                    <IonRadio value={value} labelPlacement="end" justify="start">
                      {label}
                    </IonRadio>
                  </IonItem>
                ))
              )}
            </IonRadioGroup>
          </IonList>
          <IonToolbar>
            <IonButtons slot="end">
              <IonButton color="primary" onClick={handleCancel}>
                Cancel
              </IonButton>
              <IonButton color="primary" onClick={handleOk}>
                Ok
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonContent>
      </IonModal>
    </>
  );
};
